import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  TextField,
} from "@mui/material";
import {
  CreateSupportTicketMetaOffering,
  CreateSupportTicketMetaRouting,
  CreateSupportTicketMetaService,
  Role,
  SupportDetails,
  Resource,
  TaskKind,
  User,
} from "../../../app/backendTypes";
import { Close } from "@mui/icons-material";
import { useCallback, useContext, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import Morselbar from "../Morselbar";
import SupportRoutingDisplay from "./SupportRoutingDisplay";
import SupportServiceDisplay from "./SupportServiceDisplay";
import { HttpClientContext, X_REQUEST_ID } from "../../../app/client";
import { AxiosError } from "axios";

export default function SupportDialog(props: {
  open: boolean;
  setOpen: (open: boolean) => void;
  routing: CreateSupportTicketMetaRouting;
  service: CreateSupportTicketMetaService;
  offering: CreateSupportTicketMetaOffering;
  resource: Resource;
  user: User;
  callback: () => void;
}) {
  const {
    open,
    setOpen,
    routing,
    offering,
    service,
    resource,
    user,
    callback,
  } = props;

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [createNote, setCreateNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const client = useContext(HttpClientContext);

  const createSupportTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = createNote || undefined;

    const body: TaskKind = {
      kind: "Support",
      inner: {
        details: {
          service,
          routing,
          offering,
        } as SupportDetails,
        resource,
        target: user.employee.id,
        note,
        ticket: undefined,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => {
        callback();
        setOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    callback,
    client,
    createNote,
    offering,
    override,
    resource,
    routing,
    service,
    setOpen,
    user.employee.id,
  ]);

  return (
    <>
      <Dialog open={open} fullWidth>
        <DialogTitle color="primary">
          Create support ticket
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => {
              setOpen(false);
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <SupportRoutingDisplay routing={routing} />

          <SupportServiceDisplay service={service} />

          <br />

          <TextField
            onChange={(e) => setCreateNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={3}
            maxRows={7}
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={override}
                      onChange={(e) => setOverride(e.target.checked)}
                    />
                  }
                  label="Skip approvals"
                />
              )}
            </Box>

            <Button
              onClick={() => createSupportTask()}
              disabled={loading}
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}
